import React, { useLayoutEffect, useState } from 'react';
import cs from 'classnames';

import s from './Line.module.scss';
import { IdleAnimationConfig, getIdleAnimationConfig } from './idleConfig';
import { impulseFromRightConfig } from './impulseFromRightConfig';
import {
  getImpulseFromCenterHorizontalDelay,
  getImpulseFromCenterVerticalDelay,
  impulseFromCenterConfig,
} from './impulseFromCenterConfig';

export type LineOrientation = 'horizontal' | 'vertical';

interface LineProps {
  orientation: LineOrientation;
  position: number;
  fromCenterIndex: number;
  isIdle: boolean;
}

export const Line = ({
  orientation,
  position,
  fromCenterIndex,
  isIdle,
}: LineProps) => {
  const glowIdleRef = React.useRef<HTMLDivElement>(null);

  const [canAnimateIdle, setCanAnimateIdle] = useState<boolean>(true);
  const [idleAnimationConfig, setIdleAnimationConfig] =
    useState<IdleAnimationConfig>(getIdleAnimationConfig(orientation));

  const lineClassName = cs(s.line, {
    [s.lineHorizontal]: orientation === 'horizontal',
    [s.lineHorizontalToRight]: idleAnimationConfig.direction === 'to-right',
    [s.lineHorizontalToLeft]: idleAnimationConfig.direction === 'to-left',

    [s.lineVertical]: orientation === 'vertical',
    [s.lineVerticalToTop]: idleAnimationConfig.direction === 'to-top',
    [s.lineVerticalToBottom]: idleAnimationConfig.direction === 'to-bottom',

    [s.animationIdle]: isIdle && canAnimateIdle,
  });

  const linePositionStyle: Record<string, string> = {
    [orientation === 'horizontal' ? 'top' : 'left']: `${position}px`,
    '--from-center-index': `${fromCenterIndex}`,

    '--idle-size': `${idleAnimationConfig.size}px`,
    '--idle-from-scale': idleAnimationConfig.fromScale,
    '--idle-to-scale': idleAnimationConfig.toScale,
    '--idle-from-translate': idleAnimationConfig.fromTranslate,
    '--idle-to-translate': idleAnimationConfig.toTranslate,
    '--idle-duration': `${idleAnimationConfig.duration}s`,
    '--idle-delay': `${idleAnimationConfig.delay}s`,

    '--impulse-from-right-duration': `${impulseFromRightConfig.duration}s`,
    '--impulse-from-right-delay-multiplier': `${impulseFromRightConfig.delayMultiplier}s`,

    '--impulse-from-center-duration-horizontal': `${impulseFromCenterConfig.durationHorizontal}s`,
    '--impulse-from-center-duration-vertical': `${impulseFromCenterConfig.durationVertical}s`,
    '--impulse-from-center-delay-horizontal': `${getImpulseFromCenterHorizontalDelay(
      fromCenterIndex,
    )}s`,
    '--impulse-from-center-delay-vertical': `${getImpulseFromCenterVerticalDelay(
      fromCenterIndex,
    )}s`,
  };

  useLayoutEffect(() => {
    const glowIdle = glowIdleRef.current;

    const onAnimationEnd = () => {
      setCanAnimateIdle(false);

      setIdleAnimationConfig(getIdleAnimationConfig(orientation));

      setTimeout(() => {
        setCanAnimateIdle(true);
      }, 50);
    };

    glowIdle?.addEventListener('animationend', onAnimationEnd);

    return () => {
      glowIdle?.removeEventListener('animationend', onAnimationEnd);
    };
  }, []);

  return (
    <div className={lineClassName} style={linePositionStyle}>
      <div className={s.glowIdleWrapper}>
        <div className={cs(s.glow, s.glowIdle)} ref={glowIdleRef} />
      </div>

      <div className={cs(s.glow, s.glowImpulseFromRight)} />

      <div className={cs(s.glow, s.glowImpulseFromCenterOne)} />
      <div className={cs(s.glow, s.glowImpulseFromCenterTwo)} />
    </div>
  );
};
